import { firstValueFrom } from 'rxjs'
import { PointCloudOctree } from 'potree-core'

import { Injectable } from '@angular/core'
import { MapSpace } from '@classes/MapSpace'
import { ModelSpace } from '@classes/ModelSpace'
import { Model } from '@classes/Model'
import { modelLoadMap, sceneLoadMap } from '@classes/SceneManager'
import { ProjectService } from './project.service'
import { ThumbnailPolicyService } from './thumbnail-policy.service'

import { AwsService } from './aws.service'
import { EnvironmentManagerService } from './environment-manager.service'
import { ModelService } from './model.service'
import { SceneService } from './scene.service'
import { CommandService } from './command.service'

@Injectable({
  providedIn: 'root'
})
export class ThumbnailService {
  private get _sceneManager() { return this._envManager.sceneManager }
  private get _modelSpace() { return this._envManager.modelSpace }
  private get _selectedScene() { return this._sceneService.selectedScene }
  private get _selectedModel() { return this._modelService.selectedModel }

  constructor(
    private _awsService: AwsService,
    private _modelService: ModelService,
    private _envManager: EnvironmentManagerService,
    private _sceneService: SceneService,
    private _commandService: CommandService,
    public policy: ThumbnailPolicyService,
    private _projectService: ProjectService
  ) { }

  updateSceneThumbnail() {
    const scene = this._selectedScene
    const oldThumbnail = scene.thumbnail

    return this.getThumbnail(this._modelSpace).then(async (cropped) => {
      // Show cropped preview immediately
      scene.thumbnail = cropped
      scene.updateThumbnail = false

      // Upload to S3 and store s3ref:{id}
      try {
        const file = await this._base64ToFile(cropped, 'scene-thumbnail.png')
        const s3Ref = await firstValueFrom(this._awsService.uploadAndRegister(file, 'upload-scene-thumbnail', 'thumbnail'))
        const s3RefValue = `s3ref:${s3Ref.id}`
        // Persist s3ref to DB, then restore the base64 preview in memory
        // so the UI doesn't try to render "s3ref:7" as an img src
        this._commandService.update.scene(scene, 'thumbnail', s3RefValue, oldThumbnail).subscribe({
          next: () => { scene.thumbnail = cropped },
          error: () => { scene.thumbnail = oldThumbnail }
        })
      } catch {
        // Revert to old thumbnail so base64 never gets persisted to DB
        console.error('Failed to upload scene thumbnail to S3')
        scene.thumbnail = oldThumbnail
      }
    })
  }

  async updateModelThumbnail(updateLocally = true) {
    const model = this._selectedModel
    try {
      const cropped = await this.getThumbnail(this._modelSpace)
      const preview = await this.policy.saveUserImage('model',model.id,await this.previewImage(cropped))
      if (preview) {
        model.thumbnail=preview
        if(updateLocally) this._modelService.editModelLocally(model,'update')
      }
    } catch { this.policy.view('model',model.id).status='error' }
  }

  async uploadModelThumbnail(file: File) {
    const model=this._selectedModel
    if(!file || !model) return
    try {
      const imageData=await new Promise<string>((resolve,reject)=>{
        const reader=new FileReader()
        reader.onload=()=>typeof reader.result==='string' ? resolve(reader.result) : reject(new Error('Preview image could not be read'))
        reader.onerror=reader.onabort=()=>reject(new Error('Preview image could not be read'))
        reader.readAsDataURL(file)
      })
      const image=await new Promise<HTMLImageElement>((resolve,reject)=>{
        const element=new Image()
        element.onload=()=>element.naturalWidth>0 && element.naturalHeight>0 ? resolve(element) : reject(new Error('Preview image has no dimensions'))
        element.onerror=()=>reject(new Error('Preview image could not be decoded'))
        element.src=imageData
      })
      const canvas=document.createElement('canvas')
      canvas.width=image.naturalWidth
      canvas.height=image.naturalHeight
      const context=canvas.getContext('2d')
      if(!context) throw new Error('Preview canvas unavailable')
      context.drawImage(image,0,0)
      const cropped=await this.cropScreenshot(canvas.toDataURL('image/png'),canvas.width,canvas.height)
      const preview=await this.policy.saveUserImage('model',model.id,await this.previewImage(cropped))
      if(preview) { model.thumbnail=preview; this._modelService.editModelLocally(model,'update') }
    } catch { this.policy.view('model',model.id).status='error' }
  }

  async ensureModelThumbnail(model: Model) {
    if(!model || this._selectedModel?.id!==model.id || !this._modelSpace || !modelLoadMap.get(model.id)?.loaded) return
    const preview=await this.policy.generate('model',model.id,async()=>{
      if(this._selectedModel?.id!==model.id) throw new Error('Model changed before capture')
      await this.nextRender()
      if(model.type==='point-cloud') await this.waitForVisiblePoints(model)
      if(this._selectedModel?.id!==model.id) throw new Error('Model changed before capture')
      return this.previewImage(await this.getThumbnail(this._modelSpace))
    })
    if(preview && this._selectedModel?.id===model.id) {
      model.thumbnail=preview
      this._modelService.editModelLocally(model,'update')
    }
  }

  async useAutomaticModelThumbnail() {
    const model=this._selectedModel
    try {
      await this.policy.useAutomatic('model',model.id)
      await this.ensureModelThumbnail(model)
    } catch { this.policy.view('model',model.id).status='error' }
  }

  async ensureProjectThumbnail() {
    const project=this._projectService.currentProject
    const scene=this._selectedScene
    if(!project || !scene || scene.previousID!=null || !this._envManager.isEditing || !sceneLoadMap.get(scene.id)?.loaded) return
    if(scene.type==='Map' && !this._envManager.mapSpace?.map.loaded()) return
    const preview=await this.policy.generate('project',project.id,async()=>{
      if(this._selectedScene?.id!==scene.id) throw new Error('Scene changed before capture')
      await this.nextRender()
      if(this._selectedScene?.id!==scene.id) throw new Error('Scene changed before capture')
      const clouds: PointCloudOctree[]=[]
      this._sceneManager.renderedScene.traverse(object=>{
        if(object instanceof PointCloudOctree && object.visible!==false) clouds.push(object)
      })
      await this.waitForClouds(clouds)
      if(this._selectedScene?.id!==scene.id) throw new Error('Scene changed while points were loading')
      const space=scene.type==='Map'?this._envManager.mapSpace:this._modelSpace
      return this.previewImage(await this.getThumbnail(space))
    })
    if(preview && this._projectService.currentProject?.id===project.id) {
      project.thumbnail=preview
      this._projectService.currentProject=project
    }
  }

  private async waitForVisiblePoints(model: Model) {
    const clouds: PointCloudOctree[]=[]
    this._sceneManager.getModelGroup(model.id)?.traverse(object=>{
      if(object instanceof PointCloudOctree) clouds.push(object)
    })
    if(!clouds.length) throw new Error('Point-cloud renderer is not ready')
    await this.waitForClouds(clouds)
  }

  private async waitForClouds(clouds: PointCloudOctree[]) {
    const started=performance.now()
    while(clouds.some(cloud=>!cloud.visibleNodes?.length)) {
      if(performance.now()-started>10000) throw new Error('Point-cloud geometry is not ready for a preview')
      await this.nextRender()
    }
  }

  private nextRender(): Promise<void> {
    return new Promise(resolve=>requestAnimationFrame(()=>requestAnimationFrame(()=>resolve())))
  }

  private async previewImage(preview: string) {
    return {file:await this._base64ToFile(preview,'thumbnail.png'),preview}
  }

  private getThumbnail(space: ModelSpace | MapSpace) {
    const canvasHeight = space.canvas.height
    const canvasWidth = space.canvas.width

    space.renderer.render(this._sceneManager.renderedScene, space.camera)

    return this._getCanvasScreenshot(space)
      .then((screenShot: string) => this.cropScreenshot(screenShot, canvasWidth, canvasHeight))
  }

  private _getCanvasScreenshot(space: ModelSpace | MapSpace) {
    return new Promise<string>((resolve) => {
      if (space instanceof MapSpace) {
        space.map.once('render', () => resolve(space.canvas.toDataURL('image/png')))
        space.map.triggerRepaint()
      } else {
        resolve(space.canvas.toDataURL("image/png"))
      }
    })
  }

  cropScreenshot(screenShot: string, width: number, height: number) {
    const cropCanvas = document.createElement('canvas')
    const contex = cropCanvas.getContext("2d")
    const imageElement = document.createElement('img')
    const landscape: boolean = width > height

    imageElement.src = screenShot
    cropCanvas.width = 500
    cropCanvas.height = 500
    cropCanvas.append(imageElement)

    return new Promise<string>((resolve, reject) => {
      imageElement.onerror = () => reject(new Error('Preview image could not be decoded'))
      imageElement.onload = () => {
        const srcCanvasX = landscape ? (width - height) / 2 : 0
        const srcCanvasY = landscape ? 0 : (height - width) / 2
        const srcCanvasWidth = landscape ? height : width
        const srcCanvasHeight = landscape ? height : width
        const destCanvasX = 0
        const destCanvasY = 0
        const destCanvasWidth = 500
        const destCanvasHeight = 500

        contex.drawImage(imageElement,
          srcCanvasX, srcCanvasY,
          srcCanvasWidth, srcCanvasHeight,
          destCanvasX, destCanvasY,
          destCanvasWidth, destCanvasHeight)

        resolve(cropCanvas.toDataURL("image/png"))

        cropCanvas.remove()
        imageElement.remove()
      }
    })
  }

  /** Converts a base64 data URL to a File object */
  private async _base64ToFile(base64: string, filename: string): Promise<File> {
    const res = await fetch(base64)
    const blob = await res.blob()
    return new File([blob], filename, { type: blob.type || 'image/png' })
  }
}
